import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviationDecisionDto, DeviationDecisionPreviewDto, InspectionDto } from "@/types";
import { useGetDeviationDecisionPreview } from "@/services/Inspections";
import { getApiError } from "@/utils/apiErrors";
import { DeviationDecisionsForm } from "./useDeviationDecisionsForm";

/** How long the manager has to stop typing before the decisions are priced again. */
const PREVIEW_DELAY_MS = 400;

export interface DecisionPreview {
  /** Every deviation priced with the form's decisions applied; undefined while nothing is decided, or on an error. */
  preview: DeviationDecisionPreviewDto | undefined;
  /** Why the backend couldn't price the decisions. */
  error: string | null;
}

/** The reason doesn't change a price, so it's left out: typing it would only ask again. */
const toPreviewDecision = ({ deviationId, decision, weightKg, registeredNumber }: DeviationDecisionDto) => ({
  deviationId,
  decision,
  weightKg,
  registeredNumber,
});

/**
 * Prices the manager's decisions with the backend's decision preview while the form is filled in. Costs are never
 * calculated here. A weight or number that fails the form's checks isn't sent, so the last result stays meanwhile.
 */
export const useDecisionPreview = (
  orderId: number,
  form: DeviationDecisionsForm,
  enabled: boolean
): DecisionPreview => {
  const { t } = useTranslation();
  const decisions = form.decisions.map(toPreviewDecision);
  // Compared by content: the form builds new decisions on every render.
  const key = JSON.stringify(decisions);
  // The preview takes a reject without its reason, so only a weight or number holds it back.
  const isValid = Object.keys(form.errors).every((field) => field.endsWith(".comment"));
  const [requested, setRequested] = useState<DeviationDecisionDto[]>([]);

  useEffect(() => {
    if (!isValid) return;
    const timer = setTimeout(() => setRequested(JSON.parse(key)), PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [key, isValid]);

  // Nothing decided means the saved costs apply. A request naming a deviation that's no longer pending (just saved,
  // or decided by someone else) would be refused, so it waits for the next one.
  const isActive =
    enabled &&
    decisions.length > 0 &&
    requested.length > 0 &&
    requested.every(({ deviationId }) => form.pending.some((deviation) => deviation.id === deviationId));
  const query = useGetDeviationDecisionPreview(orderId, isActive ? requested : null);

  const apiError = isActive && query.isError ? getApiError(query.error) : null;
  const error = !apiError
    ? null
    : apiError.errorCode === 182 || apiError.status === 409
      ? t("inspections:decisions.errors.notPending")
      : apiError.errorCode === 183 && apiError.reason
        ? t("inspections:costs.previewInvalid", { reason: apiError.reason })
        : t("inspections:costs.previewFailed");

  return { preview: isActive && !error ? query.data : undefined, error };
};

/** The inspection as the decision preview prices it, for the cost summary: the decisions applied, nothing saved. */
export const withPreview = (
  inspection: InspectionDto,
  preview: DeviationDecisionPreviewDto | undefined
): InspectionDto => {
  if (!preview) return inspection;
  const lines = new Map(preview.deviations.map((line) => [line.deviationId, line]));
  return {
    ...inspection,
    deviations: inspection.deviations.map((deviation) => {
      const line = lines.get(deviation.id);
      if (!line) return deviation;
      const { confirmationStatus, registeredNumber, weightKg, unitPrice, cost } = line;
      return { ...deviation, confirmationStatus, registeredNumber, weightKg, unitPrice, cost };
    }),
  };
};
